import {
  readConfig,
  getCustomerKey,
  getSessionKey,
  getTouchpoint,
  requestVoyadoJson,
  createProductCardRenderer,
  syncCustomerKey,
  sendClickNotification,
  escapeHtml,
  escapeAttr,
  safeUrl,
  formatPrice,
  normalizeSortValue,
  parseSortOptions,
  parseSortLabels,
  buildSwatchStyle,
  buildFacetColorStyle,
  translateFacetLabel,
  hasVoyadoConsent,
} from 'voyado-elevate';

const _syncOnce = syncCustomerKey();
let _loadedWithoutConsent = false;

const DEFAULT_COLLECTION_SORTS = ['RELEVANCE', 'NEWEST_FIRST', 'PRICE_ASCENDING', 'PRICE_DESCENDING', 'DISCOUNT'];

function readLandingPageConfig() {
  const element = document.getElementById('voyado-collection-config');
  const data = element ? element.dataset : {};

  return {
    pageReference: data.pageReference || window.location.pathname,
    channels: data.channels || '',
    priceId: data.priceId || '',
    stores: data.stores || '',
    templateId: data.templateId || '',
    viewId: data.viewId || 'production',
    presentCustom: data.presentCustom || '',
    presentPrices: data.presentPrices || '',
    productRules: data.productRules || '',
    disableTracking: data.disableTracking === 'true',
    productsPerPage: Math.max(parseInt(data.productsPerPage || '24', 10) || 24, 1),
    enableInfiniteScroll: data.enableInfiniteScroll === 'true',
    columnsDesktop: parseInt(data.columnsDesktop || '4', 10) || 4,
    columnsMobile: parseInt(data.columnsMobile || '2', 10) || 2,
    showImage: data.showImage !== 'false',
    imageAspectRatio: data.imageAspectRatio || 'natural',
    enableImageSlider: data.enableImageSlider === 'true',
    showPrice: data.showPrice !== 'false',
    showSwatch: data.showSwatch !== 'false',
    showFacets: data.showFacets !== 'false',
    facetsPosition: data.facetsPosition || 'drawer',
    facetsLabel: data.facetsLabel || 'Filter',
    activeFacetsLabel: data.activeFacetsLabel || 'Aktive Filter',
    clearAllLabel: data.clearAllLabel || 'Alle zurücksetzen',
    rangeApplyLabel: data.rangeApplyLabel || 'Anwenden',
    showSort: data.showSort !== 'false',
    sortLabel: data.sortLabel || 'Sortierung',
    sortOptions: parseSortOptions(data.sortOptions || '', DEFAULT_COLLECTION_SORTS),
    sortLabels: parseSortLabels(data.sortLabels || ''),
    totalHitsLabel: data.totalHitsLabel || '{n} Produkte',
    totalHitsLabelOne: data.totalHitsLabelOne || '1 Produkt',
    noResultsLabel: data.noResultsLabel || 'Keine Produkte gefunden',
    noResultsSubtext: data.noResultsSubtext || 'Bitte versuche andere Filter.',
    errorLabel: data.errorLabel || 'Fehler beim Laden',
    errorSubtext: data.errorSubtext || 'Bitte lade die Seite neu.',
    loadMoreLabel: data.loadMoreLabel || 'Mehr laden',
    boolTrueLabel: data.boolTrueLabel || 'Ja',
    boolFalseLabel: data.boolFalseLabel || 'Nein',
    enableRecommendations: data.enableRecommendations === 'true',
    recommendationsHeadingPrefix: data.recommendationsHeadingPrefix || '',
    enableInlineSearch: data.enableInlineSearch === 'true',
    inlineSearchPlaceholder: data.inlineSearchPlaceholder || 'Suche in dieser Kategorie...',
    heading: data.heading || '',
  };
}

function createEmptyState() {
  return {
    query: '',
    sort: '',
    filters: {},
    page: 1,
  };
}

class VoyadoCollectionPage {
  constructor() {
    this._root = document.querySelector('[data-voyado-collection]');
    if (!this._root) return;

    try {
      this._baseConfig = readConfig();
    } catch {
      return;
    }

    this._sectionConfig = readLandingPageConfig();
    Object.assign(this, createProductCardRenderer(() => this._sectionConfig, {
      buildSwatchStyleFn: buildSwatchStyle,
    }));
    // These renderer hooks come from the shared mixin and are required by this controller.
    if (!['_normalizeProductGroups', '_renderProductCard', '_bindProductCardContainer'].every((method) => typeof this[method] === 'function')) {
      return;
    }
    this._inlineSearchForm = this._root.querySelector('#voyado-inline-search-form');
    this._inlineSearchInput = this._root.querySelector('input[name="q"]');
    this._facetsContainer = this._root.querySelector('#voyado-filters');
    this._sortContainer = this._root.querySelector('#voyado-sort');
    this._sortWrap = this._root.querySelector('#voyado-sort-container');
    this._sortCurrentLabel = this._root.querySelector('#voyado-sort-current-label');
    this._toolbar = this._root.querySelector('#voyado-toolbar');
    this._activeFiltersContainer = this._root.querySelector('#voyado-active-filters');
    this._totalHitsContainer = this._root.querySelector('#voyado-total-hits');
    this._grid = this._root.querySelector('#voyado-product-grid') || this._root.querySelector('[ref="grid"]');
    if (this._grid && !this._grid.reveal) this._grid.reveal = () => {};
    this._loading = this._root.querySelector('#voyado-loading');
    this._sentinel = this._root.querySelector('#voyado-sentinel');
    this._noResults = this._root.querySelector('#voyado-no-results');
    this._error = this._root.querySelector('#voyado-error');
    this._pagination = this._root.querySelector('#voyado-pagination');
    this._recommendations = this._root.querySelector('#voyado-recommendations');
    this._nativeContent = this._root.querySelector('#voyado-native-collection');
    this._voyadoWrapper = this._root.querySelector('#voyado-collection-wrapper');
    this._observer = null;
    this._abortController = null;
    this._fetchGeneration = 0;
    this._isFetching = false;
    this._state = createEmptyState();
    this._facets = [];
    this._availableSorts = [];
    this._selectedFilters = [];
    this._totalHits = 0;
    this._currentPage = 1;
    this._totalPages = 1;
    this._loadedCount = 0;
    this._isLoadingVisible = false;
    this._isShowingNoResults = false;
    this._isShowingError = false;
    this._facetOpenState = {};

    if (!this._grid || !this._sentinel) {
      return;
    }

    if (this._nativeContent) this._nativeContent.hidden = true;
    if (this._voyadoWrapper) this._voyadoWrapper.hidden = false;

    this._bindEvents();
    this._state = this._readStateFromUrl();
    this._applyStateToSearchInput();
    this._replaceHistory();
    this._loadFromState('replace');
  }

  _bindEvents() {
    this._inlineSearchForm?.addEventListener('submit', (event) => {
      event.preventDefault();
      if (!this._inlineSearchInput) return;

      this._state.query = this._inlineSearchInput.value.trim();
      this._state.page = 1;
      this._pushHistory();
      this._loadFromState('replace');
    });

    this._sortContainer?.addEventListener('click', (event) => {
      const btn = event.target.closest('[data-sort-value]');
      if (!(btn instanceof HTMLElement)) return;

      this._state.sort = normalizeSortValue(btn.dataset.sortValue);
      this._state.page = 1;
      this._sortContainer.dispatchEvent(new CustomEvent('listbox:select', { bubbles: true }));
      this._pushHistory();
      this._loadFromState('replace');
    });

    this._facetsContainer?.addEventListener('change', (event) => {
      const target = event.target;
      if (!(target instanceof HTMLInputElement)) return;

      const facetId = target.dataset.facetId;
      if (!facetId) return;

      if (target.type === 'checkbox') {
        const current = Array.isArray(this._state.filters[facetId]) ? [...this._state.filters[facetId]] : [];

        if (target.checked) {
          if (!current.includes(target.value)) current.push(target.value);
          this._state.filters[facetId] = current.length > 0 ? current : [target.value];
        } else {
          const next = current.filter((value) => value !== target.value);
          if (next.length > 0) {
            this._state.filters[facetId] = next;
          } else {
            delete this._state.filters[facetId];
          }
        }

        this._state.page = 1;
        this._pushHistory();
        this._loadFromState('replace');
      }
    });

    this._facetsContainer?.addEventListener('submit', (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !form.matches('[data-range-form]')) return;

      event.preventDefault();
      const facetId = form.dataset.facetId;
      if (!facetId) return;

      const minInput = form.querySelector('[data-range-min]');
      const maxInput = form.querySelector('[data-range-max]');
      const minValue = minInput instanceof HTMLInputElement ? minInput.value.trim() : '';
      const maxValue = maxInput instanceof HTMLInputElement ? maxInput.value.trim() : '';
      const nextRange = {};

      if (minValue !== '') nextRange.min = minValue;
      if (maxValue !== '') nextRange.max = maxValue;

      if (Object.keys(nextRange).length > 0) {
        this._state.filters[facetId] = nextRange;
      } else {
        delete this._state.filters[facetId];
      }

      this._state.page = 1;
      this._pushHistory();
      this._loadFromState('replace');
    });

    this._facetsContainer?.addEventListener('click', (event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;

      const toggle = target.closest('[data-facet-toggle]');
      if (toggle) {
        const facetId = toggle.getAttribute('data-facet-toggle');
        if (!facetId) return;

        this._facetOpenState[facetId] = !this._isFacetOpen(facetId);
        this._renderFacets();
        return;
      }

      const clearButton = target.closest('[data-action="clear-all-filters"]');
      if (clearButton) {
        this._state.filters = {};
        this._state.page = 1;
        this._pushHistory();
        this._loadFromState('replace');
      }
    });

    this._activeFiltersContainer?.addEventListener('click', (event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;

      const clearButton = target.closest('[data-action="clear-all-filters"]');
      if (clearButton) {
        this._state.filters = {};
        this._state.page = 1;
        this._pushHistory();
        this._loadFromState('replace');
        return;
      }

      const removeButton = target.closest('[data-remove-filter]');
      if (!removeButton) return;

      const facetId = removeButton.getAttribute('data-facet-id');
      if (!facetId) return;

      const rangeKey = removeButton.getAttribute('data-range-key');
      if (rangeKey) {
        const currentRange = this._state.filters[facetId];
        if (currentRange && !Array.isArray(currentRange)) {
          const nextRange = { ...currentRange };
          delete nextRange[rangeKey];

          if (Object.keys(nextRange).length > 0) {
            this._state.filters[facetId] = nextRange;
          } else {
            delete this._state.filters[facetId];
          }
        }
      } else {
        const value = removeButton.getAttribute('data-filter-value');
        const current = this._state.filters[facetId];

        if (Array.isArray(current) && value) {
          const next = current.filter((item) => item !== value);
          if (next.length > 0) {
            this._state.filters[facetId] = next;
          } else {
            delete this._state.filters[facetId];
          }
        }
      }

      this._state.page = 1;
      this._pushHistory();
      this._loadFromState('replace');
    });

  this._pagination?.addEventListener('click', async (event) => {
  const target = event.target;
  if (!(target instanceof HTMLElement)) return;

  const pageButton = target.closest('[data-page]');
  if (!pageButton) return;

  const nextPage = parseInt(pageButton.getAttribute('data-page') || '', 10);
  if (!Number.isFinite(nextPage) || nextPage < 1 || nextPage === this._state.page) return;

  this._state.page = nextPage;
  this._pushHistory();

  await this._loadFromState('replace');

  requestAnimationFrame(() => {
    if (this._state.page !== nextPage ||
        this._currentPage !== nextPage ||
        !this._grid.childElementCount) return;

    this._grid.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  });
});
    this._bindProductCardContainer(this._grid);
    // Recommendations reuse the same renderer/card markup, so the shared click/cart handlers
    // need to be bound on both containers.
    this._bindProductCardContainer(this._recommendations);

    window.addEventListener('popstate', () => {
      this._state = this._readStateFromUrl();
      this._applyStateToSearchInput();
      this._loadFromState('replace');
    });
  }

  _readStateFromUrl() {
    const params = new URLSearchParams(window.location.search);
    const state = createEmptyState();

    state.query = params.get('q')?.trim() || '';
    state.sort = normalizeSortValue(params.get('sort') || '');

    const pageValue = parseInt(params.get('page') || '1', 10);
    state.page = Number.isFinite(pageValue) && pageValue > 0 ? pageValue : 1;

    for (const [key, value] of params.entries()) {
      if (!key.startsWith('f.')) continue;

      const rawKey = key.slice(2);

      if (rawKey.endsWith('.min')) {
        const facetId = rawKey.slice(0, -4);
        const current = state.filters[facetId];
        if (!current || Array.isArray(current)) state.filters[facetId] = {};
        state.filters[facetId].min = value;
        continue;
      }

      if (rawKey.endsWith('.max')) {
        const facetId = rawKey.slice(0, -4);
        const current = state.filters[facetId];
        if (!current || Array.isArray(current)) state.filters[facetId] = {};
        state.filters[facetId].max = value;
        continue;
      }

      const values = value
        .split('|')
        .map((item) => item.trim())
        .filter(Boolean);

      if (values.length > 0) {
        state.filters[rawKey] = values;
      }
    }

    return state;
  }

  _applyStateToSearchInput() {
    if (this._inlineSearchInput) {
      this._inlineSearchInput.value = this._state.query;
    }
  }

  _buildUrlSearchParams(pageOverride = this._state.page) {
    const params = new URLSearchParams();

    if (this._sectionConfig.enableInlineSearch && this._state.query) {
      params.set('q', this._state.query);
    }

    if (this._state.sort) {
      params.set('sort', this._state.sort);
    }

    Object.entries(this._state.filters).forEach(([facetId, value]) => {
      if (Array.isArray(value)) {
        if (value.length > 0) {
          params.set(`f.${facetId}`, value.join('|'));
        }
        return;
      }

      if (value.min) params.set(`f.${facetId}.min`, value.min);
      if (value.max) params.set(`f.${facetId}.max`, value.max);
    });

    if (pageOverride > 1) {
      params.set('page', String(pageOverride));
    }

    return params;
  }

  _pushHistory() {
    const params = this._buildUrlSearchParams();
    const queryString = params.toString();
    const nextUrl = queryString ? `${window.location.pathname}?${queryString}` : window.location.pathname;
    history.pushState({}, '', nextUrl);
  }

  _replaceHistory(pageOverride = this._state.page) {
    const params = this._buildUrlSearchParams(pageOverride);
    const queryString = params.toString();
    const nextUrl = queryString ? `${window.location.pathname}?${queryString}` : window.location.pathname;
    history.replaceState({}, '', nextUrl);
  }

  async _loadFromState(historyMode) {
    this._disconnectObserver();
    this._abortInFlight();
    this._clearUiStates();
    this._clearResults();
    this._loadedCount = 0;
    this._currentPage = 1;
    this._totalPages = 1;
    this._totalHits = 0;
    this._facets = [];
    this._availableSorts = [];
    this._selectedFilters = [];

    const targetPage = Math.max(this._state.page, 1);

    if (!this._sectionConfig.enableInfiniteScroll) {
      const response = await this._fetchPage(targetPage, false);
      if (!response) return;
    } else {
      for (let page = 1; page <= targetPage; page += 1) {
        const append = page > 1;
        const response = await this._fetchPage(page, append);
        if (!response) return;
      }
    }

    if (historyMode === 'replace') {
      this._replaceHistory(this._currentPage);
    }

    if (this._sectionConfig.enableInfiniteScroll && this._currentPage < this._totalPages) {
      this._connectObserver();
    }
  }

  async _fetchPage(page, append) {
    await _syncOnce;
    // The observer can intersect more than once before the first append resolves; this guard keeps
    // infinite scroll to a single in-flight append request.
    if (append && this._isFetching) return null;

    const generation = ++this._fetchGeneration;
    const controller = new AbortController();
    this._abortController = controller;
    this._isFetching = append;
    const pageSize = this._sectionConfig.productsPerPage;
    const skip = (page - 1) * pageSize;
    const params = {
      market: this._baseConfig.market,
      customerKey: getCustomerKey(),
      sessionKey: getSessionKey(),
      touchpoint: getTouchpoint(),
      locale: this._baseConfig.locale,
      pageReference: this._sectionConfig.pageReference,
      skip: String(skip),
      limit: String(pageSize),
    };

    if (this._sectionConfig.enableInlineSearch && this._state.query) params.q = this._state.query;
    if (this._sectionConfig.channels) params.channels = this._sectionConfig.channels;
    if (this._sectionConfig.priceId) params.priceId = this._sectionConfig.priceId;
    if (this._sectionConfig.stores) params.stores = this._sectionConfig.stores;
    if (this._sectionConfig.templateId) params.templateId = this._sectionConfig.templateId;
    if (this._sectionConfig.viewId && this._sectionConfig.viewId !== 'production') params.viewId = this._sectionConfig.viewId;
    if (new URLSearchParams(location.search).get('PREVIEW') === 'true') params.viewId = 'preview';
    if (this._sectionConfig.presentCustom) params.presentCustom = this._sectionConfig.presentCustom;
    if (this._sectionConfig.presentPrices) params.presentPrices = this._sectionConfig.presentPrices;
    if (this._sectionConfig.productRules) params.productRules = this._sectionConfig.productRules;
    if (this._sectionConfig.disableTracking) params.notify = 'false';
    if (this._state.sort) params.sort = this._state.sort;

    Object.entries(this._state.filters).forEach(([facetId, value]) => {
      if (Array.isArray(value)) {
        if (value.length > 0) params[`f.${facetId}`] = value.join('|');
        return;
      }

      if (value.min) params[`f.${facetId}.min`] = String(value.min);
      if (value.max) params[`f.${facetId}.max`] = String(value.max);
    });

    this._setLoading(true, !append);

    try {
      const data = await requestVoyadoJson(this._baseConfig.cluster, '/api/storefront/v3/queries/landing-page', params, {
        signal: controller.signal,
        keepalive: false,
      });
      if (generation !== this._fetchGeneration) return null;

      if (!hasVoyadoConsent()) {
        _loadedWithoutConsent = true;
      }

      this._renderResponse(data, page, append);
      return data;
    } catch (error) {
      if (error.name === 'AbortError') return null;

      if (generation === this._fetchGeneration) {
        this._showError();
      }

      return null;
    } finally {
      if (append) {
        this._isFetching = false;
      }

      if (generation === this._fetchGeneration) {
        this._setLoading(false, false);
      }
    }
  }

  _renderResponse(data, page, append) {
    const primaryList = data?.primaryList || {};
    const productGroups = this._normalizeProductGroups(primaryList.productGroups);

    if (!append) {
      const sortOptions = Array.isArray(primaryList?.sort?.options) ? primaryList.sort.options : [];

      this._facets = Array.isArray(primaryList?.facets) ? primaryList.facets : [];
      this._availableSorts = sortOptions
        .map((option) => normalizeSortValue(option?.id ?? option))
        .filter(Boolean);
      this._selectedFilters = Array.isArray(primaryList?.selectedFilters)
        ? primaryList.selectedFilters
        : Array.isArray(data?.selectedFilters)
          ? data.selectedFilters
          : [];

      if (!this._state.sort) {
        this._state.sort = normalizeSortValue(primaryList?.sort?.selected || '');
      }

      this._totalHits = this._resolveTotalHits(data, primaryList, productGroups, page);
      this._totalPages = this._resolveTotalPages(primaryList, productGroups.length, page);

      if (productGroups.length > 0) {
        this._showVoyadoLayout();
      }

      this._renderSort();
      this._renderFacets();
      this._renderTotalHits();
      if (this._toolbar) this._toolbar.hidden = false;
    }

    if (!append) {
      this._clearResults();
    }

    const cardsHtml = productGroups
      .map((group) => this._renderProductCard(group))
      .filter(Boolean)
      .join('');

    if (cardsHtml) {
      this._grid.insertAdjacentHTML('beforeend', cardsHtml);
    }

    this._loadedCount += productGroups.length;
    this._currentPage = page;
    this._state.page = page;

    this._renderActiveFilters();
    this._renderPagination();

    if (!append) {
      this._renderRecommendations(primaryList.recommendationLists);

      if (productGroups.length === 0) {
        this._showVoyadoLayout();
        this._showNoResults();
        this._disconnectObserver();
        this._sentinel.hidden = true;
        return;
      }
    }

    if (this._sectionConfig.enableInfiniteScroll) {
      const shouldObserve = this._currentPage < this._totalPages && productGroups.length > 0;
      this._sentinel.hidden = !shouldObserve;
      if (shouldObserve) {
        this._connectObserver();
      } else {
        this._disconnectObserver();
      }

      this._replaceHistory(this._currentPage);
      return;
    }

    this._sentinel.hidden = true;
  }

  _canRenderSwatches() {
    return this._sectionConfig.showSwatch && this._baseConfig.showSwatches;
  }

  _resolveTotalHits(data, primaryList, renderedProducts, page) {
    const candidates = [
      data?.totalHits,
      data?.total,
      primaryList?.pagination?.total,
      primaryList?.totalHits,
      primaryList?.total,
      primaryList?.productCount,
      primaryList?.hits,
    ];

    for (const candidate of candidates) {
      const value = Number(candidate);
      if (Number.isFinite(value) && value >= 0) {
        return value;
      }
    }

    return ((page - 1) * this._sectionConfig.productsPerPage) + renderedProducts.length;
  }

  _resolveTotalPages(primaryList, renderedCount, page) {
    const pageSize = this._sectionConfig.productsPerPage;

    // API returns totalHits directly on primaryList (no pagination object)
    if (this._totalHits > 0) {
      return Math.max(Math.ceil(this._totalHits / pageSize), 1);
    }

    const hasMoreResults = renderedCount >= pageSize;
    return hasMoreResults ? page + 1 : page;
  }

  _renderFacets() {
    if (!this._facetsContainer) return;

    if (!this._sectionConfig.showFacets || this._facets.length === 0) {
      this._facetsContainer.innerHTML = '';
      this._facetsContainer.hidden = true;
      return;
    }

    const facetsHtml = this._facets
      .map((facet) => this._renderFacetGroup(facet))
      .join('');

    this._facetsContainer.hidden = false;
    this._facetsContainer.innerHTML = `
      <div class="voyado-search__facets-groups">
        ${facetsHtml}
      </div>
    `;
  }

  _normalizeFacetType(facet) {
    const raw = String(facet?.type || '').trim().toUpperCase();
    if (raw === 'ATTRIBUTEFACET') return 'TEXT';
    if (raw === 'RANGEFACET') return 'RANGE';
    return raw;
  }

  _getFacetValues(facet, facetType) {
    if (Array.isArray(facet?.values) && facet.values.length > 0) return facet.values;
    if (Array.isArray(facet?.options) && facet.options.length > 0) return facet.options;
    if (facetType === 'CHECKBOX') {
      return [{ id: 'true', value: 'true', label: facet?.label ?? facet?.id ?? '' }];
    }
    return [];
  }

  _renderFacetGroup(facet) {
    const facetId = String(facet?.id || '');
    const facetLabel = String(facet?.label || facetId);
    const facetType = this._normalizeFacetType(facet);
    const isOpen = this._isFacetOpen(facetId);

    let content = '';

    if (['CHECKBOX', 'COLOR', 'SIZE', 'TEXT'].includes(facetType)) {
      const values = this._getFacetValues(facet, facetType);
      content = `
        <div class="voyado-search__facet-values">
          ${values
            .map((value) => {
              const valueId = String(value?.id ?? value?.value ?? '');
              const label = translateFacetLabel(String(value?.label ?? valueId), this._sectionConfig.boolTrueLabel, this._sectionConfig.boolFalseLabel);
              const count = Number(value?.count ?? 0);
              const colorStyle = facetType === 'COLOR' ? buildFacetColorStyle(value) : '';
              const selected = Array.isArray(this._state.filters[facetId]) && this._state.filters[facetId].includes(valueId);
              const valueLabel = colorStyle
                ? `
                  <span class="voyado-search__facet-value-label">
                    <span class="voyado-search__facet-swatch" aria-hidden="true" style="${escapeAttr(colorStyle)}"></span>
                    <span>${escapeHtml(label)}</span>
                  </span>
                `
                : `<span>${escapeHtml(label)}</span>`;

              return `
                <label class="voyado-search__checkbox${colorStyle ? ' voyado-search__checkbox--color' : ''}">
                  <input type="checkbox" data-facet-id="${escapeAttr(facetId)}" value="${escapeAttr(valueId)}"${selected ? ' checked' : ''}>
                  ${valueLabel}
                  <span class="text-subdued">${escapeHtml(String(count))}</span>
                </label>
              `;
            })
            .join('')}
        </div>
      `;
    } else {
      const currentRange = !Array.isArray(this._state.filters[facetId]) ? (this._state.filters[facetId] || {}) : {};
      const minValue = currentRange.min || '';
      const maxValue = currentRange.max || '';
      const minBound = facet?.min ?? '';
      const maxBound = facet?.max ?? '';

      content = `
        <form class="voyado-search__range-form" data-range-form data-facet-id="${escapeAttr(facetId)}">
          <div class="voyado-search__range-inputs">
            <input type="number" inputmode="numeric" class="input" data-range-min placeholder="${escapeAttr(String(minBound))}" value="${escapeAttr(String(minValue))}">
            <input type="number" inputmode="numeric" class="input" data-range-max placeholder="${escapeAttr(String(maxBound))}" value="${escapeAttr(String(maxValue))}">
          </div>
          <button type="submit" class="button button--secondary">${escapeHtml(this._sectionConfig.rangeApplyLabel)}</button>
        </form>
      `;
    }

    return `
      <section class="voyado-search__facet-group${isOpen ? ' is-open' : ''}">
        <button type="button" class="voyado-search__facet-toggle" data-facet-toggle="${escapeAttr(facetId)}" aria-expanded="${isOpen ? 'true' : 'false'}">
          <span>${escapeHtml(facetLabel)}</span>
          <span class="voyado-search__facet-toggle-icon" aria-hidden="true">${isOpen ? '−' : '+'}</span>
        </button>
        <div class="voyado-search__facet-panel"${isOpen ? '' : ' hidden'}>
          ${content}
        </div>
      </section>
    `;
  }

  _renderSort() {
    if (!this._sortContainer) return;

    if (!this._sectionConfig.showSort) {
      this._sortContainer.innerHTML = '';
      if (this._sortWrap) this._sortWrap.hidden = true;
      return;
    }

    const allowed = this._sectionConfig.sortOptions.length > 0
      ? this._sectionConfig.sortOptions
      : DEFAULT_COLLECTION_SORTS;

    const available = this._availableSorts.length > 0
      ? this._availableSorts
        .map((value) => normalizeSortValue(value))
        .filter((value) => value && allowed.includes(value))
      : allowed;

    const currentSort = this._state.sort || 'RELEVANCE';
    const currentLabel = this._sectionConfig.sortLabels[currentSort] || currentSort;

    if (this._sortCurrentLabel) {
      this._sortCurrentLabel.textContent = escapeHtml(this._sectionConfig.sortLabel) + ': ' + currentLabel;
    }
    if (this._sortWrap) this._sortWrap.hidden = false;

    this._sortContainer.innerHTML = available
      .map((value) => {
        const label = this._sectionConfig.sortLabels[value] || value;
        const selected = value === currentSort;
        return `<button type="button" class="popover-listbox__option group" role="option" data-sort-value="${escapeAttr(value)}"${selected ? ' aria-selected="true"' : ''}><span class="reversed-link">${escapeHtml(label)}</span></button>`;
      })
      .join('');
  }

  _renderActiveFilters() {
    if (!this._activeFiltersContainer) return;

    const chips = [];

    this._facets.forEach((facet) => {
      const facetId = String(facet?.id || '');
      const facetLabel = String(facet?.label || facetId);
      const current = this._state.filters[facetId];

      if (!current) return;

      if (Array.isArray(current)) {
        const values = Array.isArray(facet?.values) ? facet.values : [];
        current.forEach((valueId) => {
          const valueMeta = values.find((value) => String(value?.id ?? value?.value ?? '') === valueId);
          const label = translateFacetLabel(String(valueMeta?.label ?? valueId), this._sectionConfig.boolTrueLabel, this._sectionConfig.boolFalseLabel);
          chips.push(`
            <button type="button" class="voyado-search__filter-chip" data-remove-filter data-facet-id="${escapeAttr(facetId)}" data-filter-value="${escapeAttr(valueId)}">
              <span>${escapeHtml(`${facetLabel}: ${label}`)}</span>
              <span aria-hidden="true">×</span>
            </button>
          `);
        });
        return;
      }

      if (current.min) {
        chips.push(`
          <button type="button" class="voyado-search__filter-chip" data-remove-filter data-facet-id="${escapeAttr(facetId)}" data-range-key="min">
            <span>${escapeHtml(`${facetLabel}: ab ${current.min}`)}</span>
            <span aria-hidden="true">×</span>
          </button>
        `);
      }

      if (current.max) {
        chips.push(`
          <button type="button" class="voyado-search__filter-chip" data-remove-filter data-facet-id="${escapeAttr(facetId)}" data-range-key="max">
            <span>${escapeHtml(`${facetLabel}: bis ${current.max}`)}</span>
            <span aria-hidden="true">×</span>
          </button>
        `);
      }
    });

    if (chips.length === 0) {
      this._activeFiltersContainer.innerHTML = '';
      this._activeFiltersContainer.hidden = true;
      return;
    }

    this._activeFiltersContainer.hidden = false;
    this._activeFiltersContainer.innerHTML = `
      <div class="voyado-search__active-filters-header">
        <span class="smallcaps">${escapeHtml(this._sectionConfig.activeFacetsLabel)}</span>
        <button type="button" class="voyado-search__clear-all link-faded" data-action="clear-all-filters">${escapeHtml(this._sectionConfig.clearAllLabel)}</button>
      </div>
      <div class="voyado-search__active-filters-list">
        ${chips.join('')}
      </div>
    `;
  }

  _renderTotalHits() {
    if (!this._totalHitsContainer) return;

    const label = this._totalHits === 1
      ? this._sectionConfig.totalHitsLabelOne
      : this._sectionConfig.totalHitsLabel.replace('{n}', String(this._totalHits));

    this._totalHitsContainer.textContent = label;
  }

  _getPaginationItems() {
    const total = this._totalPages;
    const current = this._currentPage;

    const pageSet = new Set([
      1,
      total,
      Math.max(1, current - 1),
      current,
      Math.min(total, current + 1),
    ]);

    const sorted = Array.from(pageSet).sort((a, b) => a - b);

    const items = [];
    sorted.forEach((page, index) => {
      if (index > 0 && page - sorted[index - 1] > 1) {
        items.push('ellipsis');
      }
      items.push(page);
    });

    return items;
  }

  _renderPagination() {
    if (!this._pagination) return;

    if (this._sectionConfig.enableInfiniteScroll || this._totalPages <= 1) {
      this._pagination.innerHTML = '';
      this._pagination.hidden = true;
      return;
    }

    const items = this._getPaginationItems();

    this._pagination.hidden = false;
    this._pagination.innerHTML = `
      <div class="voyado-search__pagination-inner">
        <div class="voyado-search__pagination-buttons">
          ${items
            .map((item) => {
              if (item === 'ellipsis') {
                return '<span class="voyado-search__pagination-ellipsis">…</span>';
              }
              return `<button type="button" class="voyado-search__pagination-button${item === this._currentPage ? ' is-current' : ''}" data-page="${escapeAttr(String(item))}"${item === this._currentPage ? ' aria-current="page"' : ''}>${escapeHtml(String(item))}</button>`;
            })
            .join('')}
        </div>
      </div>
    `;
  }

  _renderRecommendations(lists) {
    if (!this._recommendations) return;

    const recommendationLists = Array.isArray(lists) ? lists : [];

    if (!this._sectionConfig.enableRecommendations || recommendationLists.length === 0) {
      this._recommendations.innerHTML = '';
      this._recommendations.hidden = true;
      return;
    }

    const sectionsHtml = recommendationLists
      .map((list) => {
        const productGroups = this._normalizeProductGroups(list?.productGroups ?? list?.products ?? []);
        if (productGroups.length === 0) return '';

        const cardsHtml = productGroups
          .map((group) => this._renderProductCard(group))
          .filter(Boolean)
          .join('');

        if (!cardsHtml) return '';

        const listName = String(list?.name ?? list?.label ?? list?.id ?? '').trim();
        const headingParts = [this._sectionConfig.recommendationsHeadingPrefix, listName].filter(Boolean);
        const heading = headingParts.join(' ').trim();

        return `
          <section class="voyado-search__secondary-results">
            ${heading ? `<div class="prose prose--tight"><h2 class="h4">${escapeHtml(heading)}</h2></div>` : ''}
            <div class="voyado-search__grid">
              ${cardsHtml}
            </div>
          </section>
        `;
      })
      .filter(Boolean)
      .join('');

    if (!sectionsHtml) {
      this._recommendations.innerHTML = '';
      this._recommendations.hidden = true;
      return;
    }

    this._recommendations.hidden = false;
    this._recommendations.innerHTML = sectionsHtml;
  }

  _showVoyadoLayout() {
    if (this._nativeContent) this._nativeContent.hidden = true;
    if (this._voyadoWrapper) this._voyadoWrapper.hidden = false;
  }

  _showNativeLayout() {
    if (this._voyadoWrapper) this._voyadoWrapper.hidden = true;
    if (this._nativeContent) this._nativeContent.hidden = false;
  }

  _connectObserver() {
    if (!this._sectionConfig.enableInfiniteScroll || !this._sentinel || this._observer) return;

    this._observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        if (this._isFetching) return;
        if (this._currentPage >= this._totalPages) return;

        this._fetchPage(this._currentPage + 1, true);
      });
    }, { rootMargin: '200px 0px' });

    this._observer.observe(this._sentinel);
  }

  _disconnectObserver() {
    if (!this._observer) return;
    this._observer.disconnect();
    this._observer = null;
  }

  _abortInFlight() {
    if (!this._abortController) return;
    this._abortController.abort();
    this._abortController = null;
  }

  _setLoading(isLoading, gridEmpty) {
    document.documentElement.dispatchEvent(new CustomEvent(isLoading ? 'theme:loading:start' : 'theme:loading:end'));
    this._isLoadingVisible = isLoading && gridEmpty;

    if (this._loading) {
      this._loading.hidden = !this._isLoadingVisible;
    }
  }

  _clearResults() {
    this._isLoadingVisible = false;
    this._isShowingNoResults = false;
    this._isShowingError = false;
    this._grid.innerHTML = '';
    this._grid.hidden = false;
    this._sentinel.hidden = true;
    if (this._noResults) this._noResults.innerHTML = '';
    if (this._error) this._error.innerHTML = '';
    if (this._recommendations) {
      this._recommendations.innerHTML = '';
      this._recommendations.hidden = true;
    }
  }

  _clearUiStates() {
    this._isLoadingVisible = false;
    this._isShowingNoResults = false;
    this._isShowingError = false;
    if (this._loading) this._loading.hidden = true;
    if (this._noResults) this._noResults.hidden = true;
    if (this._error) this._error.hidden = true;
  }

  _showNoResults() {
    this._isShowingNoResults = true;
    this._showVoyadoLayout();
    if (this._noResults) {
      this._noResults.innerHTML = this._renderStateMessage(
        this._sectionConfig.noResultsLabel,
        this._sectionConfig.noResultsSubtext,
      );
      this._noResults.hidden = false;
    }
  }

  _showError() {
    this._showVoyadoLayout();
    this._clearResults();
    this._isShowingError = true;
    if (this._error) {
      this._error.innerHTML = this._renderStateMessage(
        this._sectionConfig.errorLabel,
        this._sectionConfig.errorSubtext,
      );
      this._error.hidden = false;
    }
    this._renderPagination();
  }

  _renderStateMessage(label, subtext) {
    return `
      <div class="prose prose--tight">
        <h2 class="h4">${escapeHtml(label)}</h2>
        <p>${escapeHtml(subtext)}</p>
      </div>
    `;
  }

  _isFacetOpen(facetId) {
    if (Object.prototype.hasOwnProperty.call(this._facetOpenState, facetId)) {
      return this._facetOpenState[facetId];
    }

    this._facetOpenState[facetId] = false;
    return false;
  }
}

if (!window.__voyadoCollectionPage) {
  window.__voyadoCollectionPage = new VoyadoCollectionPage();
}

document.addEventListener('visitorConsentCollected', () => {
  if (!_loadedWithoutConsent) return;
  _loadedWithoutConsent = false;
  window.__voyadoCollectionPage?._loadFromState('replace');
}, { once: true });
